/**
 * PasswordResetScreen - reset a password with an emailed code.
 *
 * One screen, two internal steps. Step `request` collects the address and calls
 * requestPasswordReset, which returns the same generic 200 whether or not the
 * account exists, so the screen always advances. Step `confirm` collects the code
 * and a new password and calls confirmPasswordReset, which returns a fresh token
 * (the reset bumps token_version). On success it stores the token and flips
 * isAuthenticated, which swaps the navigator to the authenticated stack.
 */

import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { CodeInput } from '../../components/ui/CodeInput';
import { requestPasswordReset, confirmPasswordReset } from '../../services/authApi';
import { storeToken } from '../../services/tokenStorage';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useResendCooldown } from '../../hooks/useResendCooldown';
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage';
import { RequestError } from '../../utils/apiError';
import { isValidEmail, validatePassword } from '../../utils/validation';
import { showError, showSuccess, showInfo } from '../../utils/toast';

export function PasswordResetScreen() {
  const navigation = useNavigation();

  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | undefined>();

  const { setIsAuthenticated } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const getApiErrorMessage = useApiErrorMessage();
  const { secondsLeft, start, isCoolingDown } = useResendCooldown();

  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const adoptCooldown = (error: unknown) => {
    if (error instanceof RequestError && error.retryAfterSeconds !== undefined) {
      start(error.retryAfterSeconds);
    }
  };

  const handleRequest = async () => {
    if (!isValidEmail(email)) {
      showError(t('validation.invalidEmail'));
      return;
    }
    setSubmitting(true);
    setFieldError(undefined);
    try {
      const response = await requestPasswordReset(email);
      start(response.resend_available_in_seconds);
      setStep('confirm');
      showInfo(t('auth.checkYourEmail'));
    } catch (error) {
      adoptCooldown(error);
      showError(getApiErrorMessage(error, 'common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setFieldError(passwordValidation.errorMessage!);
      return;
    }
    if (code.length !== 6) {
      setFieldError(t('auth.enterCode'));
      return;
    }
    setSubmitting(true);
    setFieldError(undefined);
    try {
      const response = await confirmPasswordReset({
        email,
        code,
        new_password: newPassword,
      });
      await storeToken(response.access_token);
      setIsAuthenticated(true);
      showSuccess(t('auth.passwordResetSuccessful'));
    } catch (error) {
      setFieldError(getApiErrorMessage(error, 'common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // The spec's resendPasswordReset was dropped; the request endpoint already
  // expires the previous code, so resending is just another request.
  const handleResend = async () => {
    setSubmitting(true);
    try {
      const response = await requestPasswordReset(email);
      start(response.resend_available_in_seconds);
      showInfo(t('auth.checkYourEmail'));
    } catch (error) {
      adoptCooldown(error);
      showError(getApiErrorMessage(error, 'auth.errResendTooSoon'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>
        {step === 'request' ? t('auth.resetPasswordTitle') : t('auth.checkYourEmail')}
      </Text>
      <Text style={styles.subtitle}>
        {step === 'request'
          ? t('auth.resetPasswordSubtitle')
          : t('auth.verifyEmailSubtitle', { email })}
      </Text>

      <View style={styles.form}>
        {step === 'request' ? (
          <>
            <Input
              label={t('auth.email')}
              value={email}
              onChangeText={setEmail}
              placeholder={t('auth.enterYourEmail')}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <Button
              title={t('auth.sendResetCode')}
              onPress={handleRequest}
              loading={submitting}
              style={styles.primaryButton}
            />
          </>
        ) : (
          <>
            <CodeInput
              value={code}
              onChangeText={setCode}
              label={t('auth.codeLabel')}
              autoFocus
              testID="code-input"
            />
            <Input
              label={t('auth.newPassword')}
              value={newPassword}
              onChangeText={setNewPassword}
              placeholder={t('auth.enterNewPassword')}
              secureTextEntry
            />
            {fieldError && <Text style={styles.errorText}>{fieldError}</Text>}
            <Button
              title={t('auth.resetPasswordButton')}
              onPress={handleConfirm}
              loading={submitting}
              disabled={code.length !== 6}
              style={styles.primaryButton}
            />
            <Button
              title={
                isCoolingDown ? t('auth.resendIn', { seconds: secondsLeft }) : t('auth.resendCode')
              }
              onPress={handleResend}
              variant="secondary"
              loading={submitting}
              disabled={isCoolingDown || submitting}
              style={styles.secondaryButton}
            />
          </>
        )}

        <TouchableOpacity onPress={() => navigation.navigate('Login' as never)}>
          <Text style={styles.backLink}>{t('auth.backToLogin')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
      justifyContent: 'center',
      padding: 20,
      backgroundColor: theme.colors.background,
    },
    title: {
      fontSize: 32,
      fontWeight: 'bold',
      marginBottom: 8,
      textAlign: 'center',
      color: theme.colors.text,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    subtitle: {
      fontSize: 16,
      color: theme.colors.textSecondary,
      marginBottom: 32,
      textAlign: 'center',
    },
    form: {
      width: '100%',
    },
    primaryButton: {
      marginTop: 8,
    },
    secondaryButton: {
      marginTop: 12,
    },
    errorText: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.error,
      marginBottom: 8,
    },
    backLink: {
      marginTop: 24,
      textAlign: 'center',
      color: theme.colors.primary,
      fontWeight: '600',
    },
  });
}
